/**
 * Queue Store - Slice
 */

import { createSlice, PayloadAction } from '@ufc/core'
import type {
  LoadingLocation,
  LoadingLocationFilter,
  LoadingLocationFormData,
  LoadingLocationRule,
  LoadingLocationRuleFilter,
  LoadingLocationRuleFormData,
  QueueEntry,
  QueueEntryFilter,
  JoinQueueData,
  JoinQueueResponse,
  QueueDashboardData,
  QueueEvent,
} from '../domain/types'

// ============================================================================
// State Interface
// ============================================================================

export interface QueueState {
  // Loading Locations
  loadingLocations: LoadingLocation[]
  selectedLocation: LoadingLocation | null
  locationFilter: LoadingLocationFilter
  locationLoading: boolean
  locationSubmitting: boolean
  locationError: string | null

  // Queue Entries
  queueEntries: QueueEntry[]
  selectedEntry: QueueEntry | null
  entryFilter: QueueEntryFilter
  entryLoading: boolean
  entrySubmitting: boolean
  entryError: string | null

  // Join Queue Result
  joinResult: JoinQueueResponse | null
  joinLoading: boolean
  joinError: string | null

  // Dashboard
  dashboard: QueueDashboardData | null
  dashboardLoading: boolean
  dashboardError: string | null

  // Events
  recentEvents: QueueEvent[]
  eventsLoading: boolean
  eventsError: string | null

  // Expired entries for today (guard/packhouse monitor view)
  expiredEntriesToday: QueueEntry[]
  expiredEntriesLoading: boolean

  // Loading Location Rules
  loadingLocationRules: LoadingLocationRule[]
  selectedRule: LoadingLocationRule | null
  ruleFilter: LoadingLocationRuleFilter
  ruleLoading: boolean
  ruleSubmitting: boolean
  ruleError: string | null

  // Rule reference data
  cities: { documentId: string; name: string }[]
  tenants: { documentId: string; name: string }[]
  ruleReferenceDataLoading: boolean

  // Reference Data (for form dropdowns)
  variants: { documentId: string; name: string; code?: string; packaging?: 'bags' | 'bulk' }[]
  factories: { documentId: string; name: string }[]
  referenceDataLoading: boolean
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: QueueState = {
  // Loading Locations
  loadingLocations: [],
  selectedLocation: null,
  locationFilter: {
    search: '',
    locationKind: 'all',
    isActive: 'all',
  },
  locationLoading: false,
  locationSubmitting: false,
  locationError: null,

  // Queue Entries
  queueEntries: [],
  selectedEntry: null,
  entryFilter: {
    search: '',
    status: 'all',
  },
  entryLoading: false,
  entrySubmitting: false,
  entryError: null,

  // Join Queue
  joinResult: null,
  joinLoading: false,
  joinError: null,

  // Dashboard
  dashboard: null,
  dashboardLoading: false,
  dashboardError: null,

  // Events
  recentEvents: [],
  eventsLoading: false,
  eventsError: null,

  // Expired entries today
  expiredEntriesToday: [],
  expiredEntriesLoading: false,

  // Loading Location Rules
  loadingLocationRules: [],
  selectedRule: null,
  ruleFilter: {
    search: '',
    isActive: 'all',
  },
  ruleLoading: false,
  ruleSubmitting: false,
  ruleError: null,

  // Rule reference data
  cities: [],
  tenants: [],
  ruleReferenceDataLoading: false,

  // Reference Data
  variants: [],
  factories: [],
  referenceDataLoading: false,
}

// ============================================================================
// Slice
// ============================================================================

const queueSlice = createSlice({
  name: 'queue',
  initialState,
  reducers: {
    // ========================================
    // Loading Locations Actions
    // ========================================

    fetchLoadingLocations(state, _action: PayloadAction<LoadingLocationFilter | undefined>) {
      state.locationLoading = true
      state.locationError = null
    },
    fetchLoadingLocationsSuccess(state, action: PayloadAction<LoadingLocation[]>) {
      state.loadingLocations = action.payload
      state.locationLoading = false
    },
    fetchLoadingLocationsFailure(state, action: PayloadAction<string | null>) {
      state.locationLoading = false
      state.locationError = action.payload
    },

    fetchLoadingLocation(state, _action: PayloadAction<string>) {
      state.locationLoading = true
      state.locationError = null
    },
    fetchLoadingLocationSuccess(state, action: PayloadAction<LoadingLocation>) {
      state.selectedLocation = action.payload
      state.locationLoading = false
    },
    fetchLoadingLocationFailure(state, action: PayloadAction<string | null>) {
      state.locationLoading = false
      state.locationError = action.payload
    },

    createLoadingLocation(state, _action: PayloadAction<LoadingLocationFormData>) {
      state.locationSubmitting = true
      state.locationError = null
    },
    createLoadingLocationSuccess(state, action: PayloadAction<LoadingLocation>) {
      state.loadingLocations.push(action.payload)
      state.locationSubmitting = false
    },
    createLoadingLocationFailure(state, action: PayloadAction<string | null>) {
      state.locationSubmitting = false
      state.locationError = action.payload
    },

    updateLoadingLocation(
      state,
      _action: PayloadAction<{ id: string; data: Partial<LoadingLocationFormData> }>
    ) {
      state.locationSubmitting = true
      state.locationError = null
    },
    updateLoadingLocationSuccess(state, action: PayloadAction<LoadingLocation>) {
      const index = state.loadingLocations.findIndex(
        (loc) => loc.documentId === action.payload.documentId
      )
      if (index !== -1) {
        state.loadingLocations[index] = action.payload
      }
      if (state.selectedLocation?.documentId === action.payload.documentId) {
        state.selectedLocation = action.payload
      }
      state.locationSubmitting = false
    },
    updateLoadingLocationFailure(state, action: PayloadAction<string | null>) {
      state.locationSubmitting = false
      state.locationError = action.payload
    },

    deleteLoadingLocation(state, _action: PayloadAction<string>) {
      state.locationSubmitting = true
      state.locationError = null
    },
    deleteLoadingLocationSuccess(state, action: PayloadAction<string>) {
      state.loadingLocations = state.loadingLocations.filter(
        (loc) => loc.documentId !== action.payload
      )
      if (state.selectedLocation?.documentId === action.payload) {
        state.selectedLocation = null
      }
      state.locationSubmitting = false
    },
    deleteLoadingLocationFailure(state, action: PayloadAction<string | null>) {
      state.locationSubmitting = false
      state.locationError = action.payload
    },

    selectLoadingLocation(state, action: PayloadAction<LoadingLocation | null>) {
      state.selectedLocation = action.payload
    },

    setLocationFilter(state, action: PayloadAction<Partial<LoadingLocationFilter>>) {
      state.locationFilter = { ...state.locationFilter, ...action.payload }
    },

    // ========================================
    // Queue Entries Actions
    // ========================================

    fetchQueueEntries(state, _action: PayloadAction<QueueEntryFilter | undefined>) {
      state.entryLoading = true
      state.entryError = null
    },
    fetchQueueEntriesSuccess(state, action: PayloadAction<QueueEntry[]>) {
      state.queueEntries = action.payload
      state.entryLoading = false
    },
    fetchQueueEntriesFailure(state, action: PayloadAction<string>) {
      state.entryLoading = false
      state.entryError = action.payload
    },

    fetchQueueEntry(state, _action: PayloadAction<string>) {
      state.entryLoading = true
      state.entryError = null
    },
    fetchQueueEntrySuccess(state, action: PayloadAction<QueueEntry>) {
      state.selectedEntry = action.payload
      state.entryLoading = false
    },
    fetchQueueEntryFailure(state, action: PayloadAction<string>) {
      state.entryLoading = false
      state.entryError = action.payload
    },

    selectQueueEntry(state, action: PayloadAction<QueueEntry | null>) {
      state.selectedEntry = action.payload
    },

    setEntryFilter(state, action: PayloadAction<Partial<QueueEntryFilter>>) {
      state.entryFilter = { ...state.entryFilter, ...action.payload }
    },

    // ========================================
    // Join Queue Actions
    // ========================================

    joinQueue(state, _action: PayloadAction<JoinQueueData>) {
      state.joinLoading = true
      state.joinError = null
      state.joinResult = null
    },
    joinQueueSuccess(state, action: PayloadAction<JoinQueueResponse>) {
      state.joinResult = action.payload
      state.joinLoading = false
      // Add to entries list
      state.queueEntries.push(action.payload.queueEntry)
    },
    joinQueueFailure(state, action: PayloadAction<string>) {
      state.joinLoading = false
      state.joinError = action.payload
    },
    clearJoinResult(state) {
      state.joinResult = null
      state.joinError = null
    },

    // ========================================
    // Cancel Entry Actions
    // ========================================

    cancelEntry(state, _action: PayloadAction<{ id: string; reason?: string }>) {
      state.entrySubmitting = true
      state.entryError = null
    },
    cancelEntrySuccess(state, action: PayloadAction<QueueEntry>) {
      const index = state.queueEntries.findIndex(
        (e) => e.documentId === action.payload.documentId
      )
      if (index !== -1) {
        state.queueEntries[index] = action.payload
      }
      if (state.selectedEntry?.documentId === action.payload.documentId) {
        state.selectedEntry = action.payload
      }
      state.entrySubmitting = false
    },
    cancelEntryFailure(state, action: PayloadAction<string>) {
      state.entrySubmitting = false
      state.entryError = action.payload
    },

    // ========================================
    // Dashboard Actions
    // ========================================

    fetchDashboard(state, _action: PayloadAction<{ tenantId?: string; factoryId?: string } | undefined>) {
      state.dashboardLoading = true
      state.dashboardError = null
    },
    fetchDashboardSuccess(state, action: PayloadAction<QueueDashboardData>) {
      state.dashboard = action.payload
      state.dashboardLoading = false
    },
    fetchDashboardFailure(state, action: PayloadAction<string>) {
      state.dashboardLoading = false
      state.dashboardError = action.payload
    },

    // ========================================
    // Events Actions
    // ========================================

    fetchRecentEvents(state, _action: PayloadAction<{ limit?: number; tenantId?: string } | undefined>) {
      state.eventsLoading = true
      state.eventsError = null
    },
    fetchRecentEventsSuccess(state, action: PayloadAction<QueueEvent[]>) {
      state.recentEvents = action.payload
      state.eventsLoading = false
    },
    fetchRecentEventsFailure(state, action: PayloadAction<string>) {
      state.eventsLoading = false
      state.eventsError = action.payload
    },

    // ========================================
    // Refresh Queue Actions
    // ========================================

    refreshQueue(state, _action: PayloadAction<string>) {
      state.locationLoading = true
    },
    refreshQueueSuccess(state, action: PayloadAction<{ locationId: string; promotedCount: number }>) {
      state.locationLoading = false
      // Trigger re-fetch of entries would happen in saga
    },
    refreshQueueFailure(state, action: PayloadAction<string>) {
      state.locationLoading = false
      state.locationError = action.payload
    },

    // ========================================
    // Expired Entries Today Actions
    // ========================================

    fetchExpiredEntriesToday(state, _action: PayloadAction<QueueEntryFilter | undefined>) {
      state.expiredEntriesLoading = true
    },
    fetchExpiredEntriesTodaySuccess(state, action: PayloadAction<QueueEntry[]>) {
      state.expiredEntriesToday = action.payload
      state.expiredEntriesLoading = false
    },
    fetchExpiredEntriesTodayFailure(state) {
      state.expiredEntriesLoading = false
    },

    // ========================================
    // Loading Location Rules Actions
    // ========================================

    fetchLoadingLocationRules(state, _action: PayloadAction<LoadingLocationRuleFilter | undefined>) {
      state.ruleLoading = true
      state.ruleError = null
    },
    fetchLoadingLocationRulesSuccess(state, action: PayloadAction<LoadingLocationRule[]>) {
      state.loadingLocationRules = action.payload
      state.ruleLoading = false
    },
    fetchLoadingLocationRulesFailure(state, action: PayloadAction<string | null>) {
      state.ruleLoading = false
      state.ruleError = action.payload
    },

    createLoadingLocationRule(state, _action: PayloadAction<LoadingLocationRuleFormData>) {
      state.ruleSubmitting = true
      state.ruleError = null
    },
    createLoadingLocationRuleSuccess(state, action: PayloadAction<LoadingLocationRule>) {
      state.loadingLocationRules.push(action.payload)
      state.ruleSubmitting = false
    },
    createLoadingLocationRuleFailure(state, action: PayloadAction<string | null>) {
      state.ruleSubmitting = false
      state.ruleError = action.payload
    },

    updateLoadingLocationRule(
      state,
      _action: PayloadAction<{ id: string; data: Partial<LoadingLocationRuleFormData> }>
    ) {
      state.ruleSubmitting = true
      state.ruleError = null
    },
    updateLoadingLocationRuleSuccess(state, action: PayloadAction<LoadingLocationRule>) {
      const index = state.loadingLocationRules.findIndex(
        (r) => r.documentId === action.payload.documentId
      )
      if (index !== -1) {
        state.loadingLocationRules[index] = action.payload
      }
      if (state.selectedRule?.documentId === action.payload.documentId) {
        state.selectedRule = action.payload
      }
      state.ruleSubmitting = false
    },
    updateLoadingLocationRuleFailure(state, action: PayloadAction<string | null>) {
      state.ruleSubmitting = false
      state.ruleError = action.payload
    },

    deleteLoadingLocationRule(state, _action: PayloadAction<string>) {
      state.ruleSubmitting = true
      state.ruleError = null
    },
    deleteLoadingLocationRuleSuccess(state, action: PayloadAction<string>) {
      state.loadingLocationRules = state.loadingLocationRules.filter(
        (r) => r.documentId !== action.payload
      )
      if (state.selectedRule?.documentId === action.payload) {
        state.selectedRule = null
      }
      state.ruleSubmitting = false
    },
    deleteLoadingLocationRuleFailure(state, action: PayloadAction<string | null>) {
      state.ruleSubmitting = false
      state.ruleError = action.payload
    },

    selectLoadingLocationRule(state, action: PayloadAction<LoadingLocationRule | null>) {
      state.selectedRule = action.payload
    },

    setRuleFilter(state, action: PayloadAction<Partial<LoadingLocationRuleFilter>>) {
      state.ruleFilter = { ...state.ruleFilter, ...action.payload }
    },

    // ========================================
    // Rule Reference Data Actions
    // ========================================

    fetchRuleReferenceData(state) {
      state.ruleReferenceDataLoading = true
    },
    fetchRuleReferenceDataSuccess(
      state,
      action: PayloadAction<{
        cities: { documentId: string; name: string; nameEn?: string; nameAr?: string }[]
        tenants: { documentId: string; name: string }[]
      }>
    ) {
      state.cities = action.payload.cities
      state.tenants = action.payload.tenants
      state.ruleReferenceDataLoading = false
    },
    fetchRuleReferenceDataFailure(state) {
      state.ruleReferenceDataLoading = false
    },

    // ========================================
    // Clear Errors
    // ========================================

    clearErrors(state) {
      state.locationError = null
      state.entryError = null
      state.joinError = null
      state.dashboardError = null
      state.eventsError = null
      state.ruleError = null
    },

    // ========================================
    // Reference Data Actions
    // ========================================

    fetchReferenceData(state) {
      state.referenceDataLoading = true
    },
    fetchReferenceDataSuccess(
      state,
      action: PayloadAction<{
        variants: { documentId: string; name: string; code?: string; packaging?: 'bags' | 'bulk' }[]
        factories: { documentId: string; name: string }[]
      }>
    ) {
      state.variants = action.payload.variants
      state.factories = action.payload.factories
      state.referenceDataLoading = false
    },
    fetchReferenceDataFailure(state) {
      state.referenceDataLoading = false
    },
  },
})

export const queueActions = queueSlice.actions
export const queueReducer = queueSlice.reducer
